/**
 * Cell Class
 *
 * Represents a single cell (mező) in the puzzle grid.
 * Migrated from the legacy Mezo constructor function in olaszmodra.js.
 */

import type {
    CellValue,
    CellType,
    CellConstructorParams,
} from '../types/cell.types';
import { rtrim } from '../utils/string';

/** Line direction type for XML */
type LineXmlValue = 'v' | 'f' | 'vf' | '';

/**
 * Cell class representing a single cell in the puzzle grid.
 */
export class Cell {
    // Cell identification
    private readonly _id: number;
    private readonly _elementId: string;

    // Cell value and type
    private _value: CellValue | null = null;
    private _type: CellType = null;

    // Special cell states
    private _isBlack = false;
    private _isImage = false;
    private _isEmpty = false;
    private _isKnown: boolean | null = null;

    // Border lines
    private _hasVerticalLine = false;
    private _hasHorizontalLine = false;
    private _hasWeakVerticalLine = false;
    private _hasWeakHorizontalLine = false;
    private _verticalLineEnabled = true;
    private _horizontalLineEnabled = true;

    // Clues/definitions
    private _verticalClue: string | null = null;
    private _horizontalClue: string | null = null;
    private _verticalBackClue: string | null = null;
    private _horizontalBackClue: string | null = null;

    // Expressions for pattern matching
    private _verticalExpression: string | null = null;
    private _horizontalExpression: string | null = null;
    private _verticalBackExpression: string | null = null;
    private _horizontalBackExpression: string | null = null;

    // Expression error states
    private _verticalExpressionError = false;
    private _horizontalExpressionError = false;
    private _verticalBackExpressionError = false;
    private _horizontalBackExpressionError = false;

    // Bridge position (for bridge cells)
    private _bridgePosition: number | null = null;

    // Styling
    private _style = '';

    constructor(params: CellConstructorParams) {
        this._id = params.id;
        this._elementId = `mezo-${params.id}`;

        // Set initial values
        this.setValue(params.value);
        this.setType(params.type);
        this.setLine(params.line);
        this.setWeakLine(params.weakLine);
        this.setVerticalClue(params.verticalClue);
        this.setHorizontalClue(params.horizontalClue);
        this.setVerticalBackClue(params.verticalBackClue);
        this.setHorizontalBackClue(params.horizontalBackClue);
        this.setVerticalExpression(params.verticalExpression);
        this.setHorizontalExpression(params.horizontalExpression);
        this.setVerticalBackExpression(params.verticalBackExpression);
        this.setHorizontalBackExpression(params.horizontalBackExpression);
        this.setBridgePosition(params.bridgePosition);
        this.setKnown(params.isKnown);
    }

    // ============================================================================
    // Getters - Identification
    // ============================================================================

    get id(): number {
        return this._id;
    }

    get elementId(): string {
        return this._elementId;
    }

    // ============================================================================
    // Value and Type
    // ============================================================================

    get value(): CellValue | null {
        return this._value;
    }

    get type(): CellType {
        return this._type;
    }

    get isBlack(): boolean {
        return this._isBlack;
    }

    get isImage(): boolean {
        return this._isImage;
    }

    get isEmpty(): boolean {
        return this._isEmpty;
    }

    get isColored(): boolean {
        return this._type === 'colored';
    }

    get isBridge(): boolean {
        return this._type === 'bridge';
    }

    get isKnown(): boolean {
        return this._isKnown === true;
    }

    setValue(value: CellValue): void {
        this._value = value;
        this._isBlack = false;
        this._isImage = false;
        this._isEmpty = false;

        switch (value) {
            case '#':
                this._isBlack = true;
                break;
            case '@':
                this._isImage = true;
                break;
            case '-':
                this._isEmpty = true;
                break;
        }
    }

    setType(type: string): void {
        if (type === '') {
            this._type = null;
        } else if (type === 'színes') {
            this._type = 'colored';
        } else if (type === 'híd') {
            this._type = 'bridge';
        } else {
            this._type = null;
        }
    }

    setKnown(isKnown: number): void {
        if (isKnown === 1) {
            this._isKnown = true;
        } else if (this._isKnown === null) {
            this._isKnown = false;
        } else {
            this._isKnown = !this._isKnown;
        }
    }

    // ============================================================================
    // Toggle Methods
    // ============================================================================

    toggleBlack(): void {
        const wasBlack = this._isBlack;
        this.clearCell();
        if (!wasBlack) {
            this._value = '#';
            this._isBlack = true;
        }
    }

    toggleImage(): void {
        const wasImage = this._isImage;
        this.clearCell();
        if (!wasImage) {
            this._value = '@';
            this._isImage = true;
        }
    }

    toggleEmpty(): void {
        const wasEmpty = this._isEmpty;
        this.clearCell();
        if (!wasEmpty) {
            this._value = '-';
            this._isEmpty = true;
        }
    }

    toggleColored(): void {
        this._type = this._type === 'colored' ? null : 'colored';
    }

    toggleBridge(): void {
        this._type = this._type === 'bridge' ? null : 'bridge';
    }

    toggleVerticalLine(): void {
        this._hasVerticalLine = !this._hasVerticalLine;
    }

    toggleHorizontalLine(): void {
        this._hasHorizontalLine = !this._hasHorizontalLine;
    }

    toggleKnown(): void {
        this._isKnown = !this._isKnown;
    }

    // ============================================================================
    // Line Properties
    // ============================================================================

    get hasVerticalLine(): boolean {
        return this._hasVerticalLine;
    }

    get hasHorizontalLine(): boolean {
        return this._hasHorizontalLine;
    }

    get hasWeakVerticalLine(): boolean {
        return this._hasWeakVerticalLine;
    }

    get hasWeakHorizontalLine(): boolean {
        return this._hasWeakHorizontalLine;
    }

    get verticalLineEnabled(): boolean {
        return this._verticalLineEnabled;
    }

    get horizontalLineEnabled(): boolean {
        return this._horizontalLineEnabled;
    }

    disableVerticalLine(): void {
        this._verticalLineEnabled = false;
    }

    disableHorizontalLine(): void {
        this._horizontalLineEnabled = false;
    }

    private setLine(line: string): void {
        switch (line) {
            case 'v':
                this._hasVerticalLine = true;
                break;
            case 'f':
                this._hasHorizontalLine = true;
                break;
            case 'vf':
                this._hasVerticalLine = true;
                this._hasHorizontalLine = true;
                break;
        }
    }

    private setWeakLine(weakLine: string): void {
        switch (weakLine) {
            case 'v':
                this._hasWeakVerticalLine = true;
                break;
            case 'f':
                this._hasWeakHorizontalLine = true;
                break;
            case 'vf':
                this._hasWeakVerticalLine = true;
                this._hasWeakHorizontalLine = true;
                break;
        }
    }

    // ============================================================================
    // Clues
    // ============================================================================

    get verticalClue(): string | null {
        return this._verticalClue;
    }

    get horizontalClue(): string | null {
        return this._horizontalClue;
    }

    get verticalBackClue(): string | null {
        return this._verticalBackClue;
    }

    get horizontalBackClue(): string | null {
        return this._horizontalBackClue;
    }

    get hasVerticalClue(): boolean {
        return this._verticalClue !== null;
    }

    get hasHorizontalClue(): boolean {
        return this._horizontalClue !== null;
    }

    get hasVerticalBackClue(): boolean {
        return this._verticalBackClue !== null;
    }

    get hasHorizontalBackClue(): boolean {
        return this._horizontalBackClue !== null;
    }

    setVerticalClue(clue: string): void {
        this._verticalClue = clue === '' ? null : clue;
    }

    setHorizontalClue(clue: string): void {
        this._horizontalClue = clue === '' ? null : clue;
    }

    setVerticalBackClue(clue: string): void {
        this._verticalBackClue = clue === '' ? null : clue;
    }

    setHorizontalBackClue(clue: string): void {
        this._horizontalBackClue = clue === '' ? null : clue;
    }

    // ============================================================================
    // Expressions
    // ============================================================================

    get verticalExpression(): string | null {
        return this._verticalExpression;
    }

    get horizontalExpression(): string | null {
        return this._horizontalExpression;
    }

    get verticalBackExpression(): string | null {
        return this._verticalBackExpression;
    }

    get horizontalBackExpression(): string | null {
        return this._horizontalBackExpression;
    }

    setVerticalExpression(expr: string): void {
        this._verticalExpression = expr === '' ? null : expr;
    }

    setHorizontalExpression(expr: string): void {
        this._horizontalExpression = expr === '' ? null : expr;
    }

    setVerticalBackExpression(expr: string): void {
        this._verticalBackExpression = expr === '' ? null : expr;
    }

    setHorizontalBackExpression(expr: string): void {
        this._horizontalBackExpression = expr === '' ? null : expr;
    }

    // ============================================================================
    // Expression Errors
    // ============================================================================

    get hasVerticalExpressionError(): boolean {
        return this._verticalExpressionError;
    }

    get hasHorizontalExpressionError(): boolean {
        return this._horizontalExpressionError;
    }

    get hasVerticalBackExpressionError(): boolean {
        return this._verticalBackExpressionError;
    }

    get hasHorizontalBackExpressionError(): boolean {
        return this._horizontalBackExpressionError;
    }

    get hasExpressionError(): boolean {
        return (
            this._verticalExpressionError ||
            this._horizontalExpressionError ||
            this._verticalBackExpressionError ||
            this._horizontalBackExpressionError
        );
    }

    enableVerticalExpressionError(): void {
        this._verticalExpressionError = true;
    }

    disableVerticalExpressionError(): void {
        this._verticalExpressionError = false;
    }

    enableHorizontalExpressionError(): void {
        this._horizontalExpressionError = true;
    }

    disableHorizontalExpressionError(): void {
        this._horizontalExpressionError = false;
    }

    enableVerticalBackExpressionError(): void {
        this._verticalBackExpressionError = true;
    }

    disableVerticalBackExpressionError(): void {
        this._verticalBackExpressionError = false;
    }

    enableHorizontalBackExpressionError(): void {
        this._horizontalBackExpressionError = true;
    }

    disableHorizontalBackExpressionError(): void {
        this._horizontalBackExpressionError = false;
    }

    // ============================================================================
    // Bridge Position
    // ============================================================================

    get bridgePosition(): number | null {
        return this._bridgePosition;
    }

    setBridgePosition(position: string): void {
        if (position === '') {
            this._bridgePosition = 0;
        } else {
            const parsed = parseInt(position, 10);
            this._bridgePosition = isNaN(parsed) ? 0 : parsed;
        }
    }

    // ============================================================================
    // Styling
    // ============================================================================

    get style(): string {
        return this._style;
    }

    setRowBreak(): void {
        this._style += 'clear:both; float:left; ';
    }

    setBorderLeft(): void {
        this._style += 'border-left:1px solid rgb(213, 213, 213); ';
    }

    setBorderTop(): void {
        this._style += 'border-top:1px solid rgb(213, 213, 213); ';
    }

    // ============================================================================
    // Clear/Reset Methods
    // ============================================================================

    clearCell(): void {
        this._value = null;
        this._type = null;
        this._verticalClue = null;
        this._horizontalClue = null;
        this._verticalBackClue = null;
        this._horizontalBackClue = null;
        this._hasVerticalLine = false;
        this._hasHorizontalLine = false;
        this._isBlack = false;
        this._isImage = false;
        this._isEmpty = false;
        this._verticalExpressionError = false;
        this._horizontalExpressionError = false;
        this._verticalBackExpressionError = false;
        this._horizontalBackExpressionError = false;
        this._verticalExpression = null;
        this._horizontalExpression = null;
        this._verticalBackExpression = null;
        this._horizontalBackExpression = null;
        this._bridgePosition = null;
    }

    clearValue(): void {
        this._value = null;
        this._type = null;
        this._isBlack = false;
        this._isImage = false;
        this._isEmpty = false;
    }

    // ============================================================================
    // Background Image
    // ============================================================================

    getBackgroundImage(
        isHorizontal?: boolean,
        onlyWarning = false,
        imagePath = '/img/',
    ): string {
        const images: string[] = [];

        const shouldShowVertical =
            isHorizontal === undefined ||
            isHorizontal ||
            this._verticalExpressionError ||
            this._verticalBackExpressionError;

        const shouldShowHorizontal =
            isHorizontal === undefined ||
            !isHorizontal ||
            this._horizontalExpressionError ||
            this._horizontalBackExpressionError;

        if (shouldShowVertical) {
            if (this.hasVerticalClue) {
                if (this._verticalExpressionError) {
                    images.push(`url(${imagePath}vmegh_warning.png)`);
                }
                if (!onlyWarning) {
                    images.push(`url(${imagePath}vmegh.png)`);
                }
            }

            if (this.hasVerticalBackClue) {
                if (this._verticalBackExpressionError) {
                    images.push(`url(${imagePath}vhmegh_warning.png)`);
                }
                if (!onlyWarning) {
                    images.push(`url(${imagePath}vhmegh.png)`);
                }
            }
        }

        if (shouldShowHorizontal) {
            if (this.hasHorizontalClue) {
                if (this._horizontalExpressionError) {
                    images.push(`url(${imagePath}fmegh_warning.png)`);
                }
                if (!onlyWarning) {
                    images.push(`url(${imagePath}fmegh.png)`);
                }
            }

            if (this.hasHorizontalBackClue) {
                if (this._horizontalBackExpressionError) {
                    images.push(`url(${imagePath}fhmegh_warning.png)`);
                }
                if (!onlyWarning) {
                    images.push(`url(${imagePath}fhmegh.png)`);
                }
            }
        }

        return images.join(', ');
    }

    // ============================================================================
    // Render to HTML
    // ============================================================================

    toHtml(
        showClueMarking: boolean,
        isHorizontal: boolean,
        _knownMarking = true,
        _validCharPattern = '',
    ): string {
        let className = 'mező ';
        let displayValue = this._value ?? '';

        // Get background images
        const backgroundImage = showClueMarking
            ? this.getBackgroundImage(isHorizontal)
            : this.getBackgroundImage(isHorizontal, true);

        let style = this._style;

        if (backgroundImage !== '') {
            style += `background-image:${backgroundImage}; `;
        }

        if (this._isKnown) {
            style += 'font-weight:bold; ';
        }

        // Cell type classes
        if (this._isBlack) {
            className += 'fekete ';
            displayValue = '';
        }
        if (this._isImage) {
            className += 'ábra ';
            displayValue = '';
        }
        if (this._isEmpty) {
            className += 'üres ';
            displayValue = '';
        }

        // Note: knownMarking visibility is handled as a POST-render operation
        // by ViewOperations.toggleKnownMarking(), NOT during initial render.
        // The legacy ismertJelolesek() function also works this way.

        // Type class (colored/bridge)
        if (this._type === 'colored') {
            className += 'színes ';
        } else if (this._type === 'bridge') {
            className += 'híd ';
        }

        // Line classes
        const hasVLine = this._hasVerticalLine || this._hasWeakVerticalLine;
        const hasHLine = this._hasHorizontalLine || this._hasWeakHorizontalLine;

        if (hasVLine && hasHLine) {
            className += 'vvonal fvonal ';
        } else if (hasVLine) {
            className += 'vvonal ';
        } else if (hasHLine) {
            className += 'fvonal ';
        }

        const classAttr =
            className !== '' ? ` class="${rtrim(className)}"` : '';
        const styleAttr = style !== '' ? ` style="${rtrim(style)}"` : '';

        return `<div id="${this._elementId}"${classAttr}${styleAttr}>${displayValue}</div>`;
    }

    // ============================================================================
    // Serialize to XML
    // ============================================================================

    toXml(): string {
        let attributes = '';

        // Line attributes
        const lineValue = this.getLineXmlValue();
        if (lineValue) {
            attributes += ` vonal="${lineValue}"`;
        }

        // Weak line attributes
        const weakLineValue = this.getWeakLineXmlValue();
        if (weakLineValue) {
            attributes += ` gyengevonal="${weakLineValue}"`;
        }

        // Vertical clue
        if (this.hasVerticalClue) {
            attributes += ` vkifejezés="${this._verticalExpression ?? ''}"`;
            attributes += ` vmeghatározás="${this._verticalClue}"`;
        }

        // Horizontal clue
        if (this.hasHorizontalClue) {
            attributes += ` fkifejezés="${this._horizontalExpression ?? ''}"`;
            attributes += ` fmeghatározás="${this._horizontalClue}"`;
        }

        // Vertical back clue (bridge)
        if (this.hasVerticalBackClue) {
            attributes += ` vhkifejezés="${
                this._verticalBackExpression ?? ''
            }"`;
            attributes += ` vhmeghatározás="${this._verticalBackClue}"`;
            attributes += ` hídpozíció="${this._bridgePosition ?? 0}"`;
        }

        // Horizontal back clue (bridge)
        if (this.hasHorizontalBackClue) {
            attributes += ` fhkifejezés="${
                this._horizontalBackExpression ?? ''
            }"`;
            attributes += ` fhmeghatározás="${this._horizontalBackClue}"`;
            attributes += ` hídpozíció="${this._bridgePosition ?? 0}"`;
        }

        // Type
        if (this._type === 'colored') {
            attributes += ' típus="színes"';
        } else if (this._type === 'bridge') {
            attributes += ' típus="híd"';
        }

        // Known state
        if (this._isKnown) {
            attributes += ' ismert="1"';
        }

        const value = this._value ?? '';
        return `<mező${attributes}>${value}</mező>`;
    }

    private getLineXmlValue(): LineXmlValue {
        if (this._hasVerticalLine && this._hasHorizontalLine) {
            return 'vf';
        } else if (this._hasVerticalLine) {
            return 'v';
        } else if (this._hasHorizontalLine) {
            return 'f';
        }
        return '';
    }

    private getWeakLineXmlValue(): LineXmlValue {
        if (this._hasWeakVerticalLine && this._hasWeakHorizontalLine) {
            return 'vf';
        } else if (this._hasWeakVerticalLine) {
            return 'v';
        } else if (this._hasWeakHorizontalLine) {
            return 'f';
        }
        return '';
    }
}
